import React, { useEffect, useMemo, useState } from "react";

import {
	Button,
	InfoMessage,
	Table,
	TEditFields,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { useCanCreateInWorkspace } from "@/hooks/useCanCreateInWorkspace";
import { useMonthPeriods } from "@/hooks/useMonthPeriods";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useProjectsFilters } from "@/hooks/useProjectsFilters";

import { ProjectFormModal } from "@/components/organisms/ProjectFormModal/ProjectFormModal";
import { PageLayout } from "@/components/templates";

import {
	clientIdOf,
	filterProjects,
	groupProjectsByClient,
} from "@/utils/UProjects";
import { groupByWorkspace, workspaceLabel } from "@/utils/UWorkspaces";

import { INTERNAL_CLIENT_ID } from "@/constants/CProjects";
import { projectKeys } from "@/constants/CQueryKeys";

import {
	TCompany,
	TProjectClient,
	TProjectsDisplay,
	TProjectSummary,
} from "@/types";

import { PROJECT_LIST_COLUMNS } from "./ProjectListView.columns";
import { ProjectsByClient } from "./components/ProjectsByClient";

const EMPTY: TProjectSummary[] = [];
const DISPLAYS: TProjectsDisplay[] = ["grouped", "list"];

/** Who the "New project" modal opens on: a client, the internal group, or nothing - and in which workspace. */
type TCreateTarget = {
	client: TProjectClient | null;
	internal: boolean;
	workspaceId?: number;
};

/**
 * `/projects` - every project of the workspace the user reaches, with
 * this month's figures, by client (default) or as one table. Search,
 * client choice, archived switch and display live in the URL through
 * useProjectsFilters; the filtering itself is client-side (a workspace
 * holds 50 projects at most). `?new=1` opens the new-project modal on
 * arrival.
 */
export const ProjectListView: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { current } = useMonthPeriods();
	const gate = usePlanGate("projects");
	const filtersContext = useProjectsFilters();
	const { filters, setFilter } = filtersContext;

	const [createTarget, setCreateTarget] = useState<TCreateTarget | null>(
		null
	);

	useEffect(() => {
		if (searchParams.get("new") !== "1") return;
		setCreateTarget({ client: null, internal: false });
		// The param did its job; a reload must not reopen the modal.
		const next = new URLSearchParams(searchParams);
		next.delete("new");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	const { isLoading, error, data } = useQuery({
		queryKey: projectKeys.listSummary(current),
		queryFn: () => ProjectsService.listSummary(current),
	});
	const projects = data ?? EMPTY;

	// The client choices come from the projects themselves: a client with
	// no project has nothing to filter.
	const clientNames = useMemo(() => {
		const names = new Map<number, string>();
		for (const project of projects) {
			names.set(
				clientIdOf(project),
				project.client?.name ?? t("projects.internal")
			);
		}
		return names;
	}, [projects]);

	const filterFields = useMemo((): TEditFields => {
		const options = [...clientNames.entries()]
			.sort(([a, aName], [b, bName]) =>
				a === INTERNAL_CLIENT_ID
					? 1
					: b === INTERNAL_CLIENT_ID
						? -1
						: aName.localeCompare(bName)
			)
			.map(([value, label]) => ({ value, label }));

		return [
			{
				name: "clientIds",
				type: "select",
				label: t("projects.filters.client"),
				multiple: true,
				options,
				valueDisplay: (id: unknown) =>
					clientNames.get(id as number) ?? String(id),
			},
			{
				name: "archived",
				type: "toggle",
				label: t("projects.filters.archived"),
				chip: (archived: unknown) =>
					archived ? t("projects.filters.archivedChip") : null,
			},
		] as TEditFields;
	}, [clientNames]);

	const visible = useMemo(
		() => filterProjects(projects, filters),
		[projects, filters]
	);
	// A block per workspace (the user's own first), by client inside.
	const workspaceGroups = useMemo(
		() =>
			groupByWorkspace(visible).map((group) => ({
				...group,
				clients: groupProjectsByClient(group.items),
			})),
		[visible]
	);
	const onlyMine =
		workspaceGroups.length === 1 &&
		(!workspaceGroups[0].workspace || workspaceGroups[0].workspace.mine);
	const columns = useMemo(() => PROJECT_LIST_COLUMNS(!onlyMine), [onlyMine]);

	const createButton = gate.button({
		label: t("projects.new"),
		color: "primary" as const,
		picto: "add" as const,
		onClick: () => setCreateTarget({ client: null, internal: false }),
	});

	const { canCreateIn } = useCanCreateInWorkspace();
	const canCreate = canCreateIn();
	const isEmpty = !isLoading && projects.length === 0;

	return (
		<>
			<PageLayout
				title={t("projects.title")}
				isLoading={isLoading}
				error={!!error}
				buttons={canCreate ? [createButton] : []}
				actionsPosition="aboveTabs"
				filters={filterFields}
				// PageHeader types the context over any slice; a concrete
				// slice's setFilters does not fit that signature (parameter
				// contravariance), though it is what the header calls it with.
				filtersContext={filtersContext as never}
				searchValue={filters.search}
				onSearchChange={(value) => setFilter("search", value)}
				searchInputProps={{
					placeholder: t("projects.list.searchPlaceholder"),
				}}
				tabs={DISPLAYS.map((value) => ({
					value,
					label: t(`projects.list.display.${value}`),
				}))}
				selectedTabId={filters.display}
				onSelectTab={(value) =>
					setFilter("display", value as TProjectsDisplay)
				}
				countMessage={
					data
						? t("projects.list.count", { count: visible.length })
						: undefined
				}
			>
				{isEmpty ? (
					<div
						className="flex flex-col items-start gap-4"
						data-testid="projects-empty"
					>
						<InfoMessage>
							<span className="flex flex-col gap-1">
								<span className="font-medium">
									{t("projects.list.empty")}
								</span>
								<span>{t("projects.emptyHint")}</span>
							</span>
						</InfoMessage>
						{canCreate && (
							<Button {...createButton}>
								{createButton.label}
							</Button>
						)}
					</div>
				) : visible.length === 0 ? (
					<InfoMessage data-testid="projects-no-results">
						{t("projects.list.noResults")}
					</InfoMessage>
				) : "list" === filters.display ? (
					<section data-testid="projects-table">
						<Table
							items={visible}
							getItemKey={(project) => project.id}
							onRowClick={(project) =>
								navigate(`/projects/${project.id}`)
							}
							columns={columns}
						/>
					</section>
				) : (
					<div className="flex flex-col gap-8">
						{workspaceGroups.map((group) => {
							const workspace = group.workspace;
							const mine = !workspace || workspace.mine;
							const key = mine ? "own" : workspace.id;
							return (
								<section
									key={key}
									data-testid={`workspace-group-${key}`}
									className="flex flex-col gap-3"
								>
									{!onlyMine && (
										<h2 className="text-lg font-semibold">
											{workspaceLabel(
												workspace ?? {
													name: null,
													ownerName: "",
													mine: true,
												}
											)}
										</h2>
									)}
									<ProjectsByClient
										groups={group.clients}
										planGated={mine}
										onCreate={
											canCreateIn(workspace?.id)
												? (client) =>
														setCreateTarget({
															client,
															internal: !client,
															workspaceId:
																workspace?.id,
														})
												: undefined
										}
									/>
								</section>
							);
						})}
					</div>
				)}
			</PageLayout>
			<ProjectFormModal
				open={!!createTarget}
				// The form wants a TCompany; it only reads the client's id and
				// name (see toProjectFormValues).
				client={(createTarget?.client as TCompany | null) ?? null}
				internal={createTarget?.internal}
				workspaceId={createTarget?.workspaceId}
				onClose={() => setCreateTarget(null)}
				onSaved={(project) => navigate(`/projects/${project.id}`)}
			/>
		</>
	);
};
